import React from 'react';
import { SignaGlyph } from '../pa/SignaGlyph';
import { PersonAvatar } from '../today/PersonAvatar';
import type { MemoryContext, MemoryItem } from '../../lib/memory-api';

/**
 * Right-side catch-up column inside the Focus pane. Shared between email and
 * todo focus views; `kind` controls which sections render.
 */

export type CatchupKind = 'email' | 'todo' | 'note';

interface CatchupPaneProps {
  kind: CatchupKind;
  context: MemoryContext | null;
  loading?: boolean;
  error?: string | null;
  // Email-kind extras
  personName?: string;
  personEmail?: string;
  personRole?: string;
  // Todo-kind extras
  todoNarrative?: React.ReactNode;
}

export function CatchupPane({
  kind,
  context,
  loading = false,
  error = null,
  personName,
  personEmail,
  personRole,
  todoNarrative,
}: CatchupPaneProps) {
  return (
    <div
      style={{
        width: 360,
        borderLeft: '1px solid var(--rule)',
        background: 'var(--catch-up-bg)',
        overflowY: 'auto',
        flexShrink: 0,
      }}
    >
      <div style={{ padding: '24px 24px 40px' }}>
        <Label>
          <SignaGlyph size={14} pulse />
          <span style={{ marginLeft: 8 }}>Catch-up from memory</span>
        </Label>

        {loading ? (
          <Skeleton />
        ) : error ? (
          <ErrorBlock message={error} />
        ) : kind === 'email' ? (
          <EmailCatchup
            personName={personName ?? ''}
            personEmail={personEmail}
            personRole={personRole}
            context={context}
          />
        ) : kind === 'todo' ? (
          <TodoCatchup narrative={todoNarrative} context={context} />
        ) : (
          <NoteCatchup context={context} />
        )}
      </div>
    </div>
  );
}

// Email catchup

function EmailCatchup({
  personName,
  personEmail,
  personRole,
  context,
}: {
  personName: string;
  personEmail?: string;
  personRole?: string;
  context: MemoryContext | null;
}) {
  const narrative = buildEmailNarrative(personName, context);
  const facts = (context?.person ?? []).slice(0, 4);
  const timeline = buildTimeline(context);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 16,
          marginBottom: 20,
        }}
      >
        <PersonAvatar name={personName} email={personEmail} size={44} />
        <div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 17,
              letterSpacing: '-0.3px',
              color: 'var(--ink)',
              fontWeight: 600,
            }}
          >
            {personName}
          </div>
          {(personRole || personEmail) && (
            <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
              {personRole ?? personEmail}
            </div>
          )}
        </div>
      </div>

      {narrative && (
        <div
          style={{
            padding: '14px 16px',
            background: 'var(--paper)',
            borderRadius: 10,
            border: '1px solid var(--rule)',
            fontSize: 13.5,
            lineHeight: 1.6,
            color: 'var(--ink)',
            marginBottom: 24,
            fontFamily: 'var(--font-display)',
            textWrap: 'pretty',
          }}
        >
          {narrative}
        </div>
      )}

      {facts.length > 0 && (
        <>
          <SectionLabel>What I remember</SectionLabel>
          <div style={{ marginBottom: 24 }}>
            {facts.map((f, i) => (
              <Bullet key={f.id ?? `fact-${i}`}>{f.content}</Bullet>
            ))}
          </div>
        </>
      )}

      {timeline.length > 0 && (
        <>
          <SectionLabel>
            Timeline - last {Math.min(timeline.length, 4)} touches
          </SectionLabel>
          <Timeline items={timeline.slice(0, 4)} />
        </>
      )}

      {!narrative && facts.length === 0 && timeline.length === 0 && (
        <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-faint)', lineHeight: 1.6 }}>
          Nothing stored about {personName || 'this person'} yet. I will build
          context as you exchange emails.
        </div>
      )}
    </>
  );
}

// Todo catchup

function TodoCatchup({
  narrative,
  context,
}: {
  narrative: React.ReactNode;
  context: MemoryContext | null;
}) {
  const timeline = buildTimeline(context);
  const people = (context?.person ?? []).slice(0, 3);

  return (
    <>
      {narrative && (
        <div
          style={{
            padding: '14px 16px',
            background: 'var(--paper)',
            borderRadius: 10,
            border: '1px solid var(--rule)',
            fontSize: 13.5,
            lineHeight: 1.6,
            color: 'var(--ink)',
            marginTop: 16,
            marginBottom: 24,
            fontFamily: 'var(--font-display)',
            textWrap: 'pretty',
          }}
        >
          {narrative}
        </div>
      )}

      {people.length > 0 && (
        <>
          <SectionLabel>People involved</SectionLabel>
          <div style={{ marginBottom: 24 }}>
            {people.map((p, i) => (
              <Bullet key={p.id ?? `p-${i}`}>{p.content}</Bullet>
            ))}
          </div>
        </>
      )}

      {timeline.length > 0 && (
        <>
          <SectionLabel>Pulled from memory</SectionLabel>
          <Timeline items={timeline.slice(0, 5)} />
        </>
      )}

      {!narrative && timeline.length === 0 && people.length === 0 && (
        <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-faint)', lineHeight: 1.6 }}>
          No context stored yet for this todo.
        </div>
      )}
    </>
  );
}

// Note catchup

function NoteCatchup({ context }: { context: MemoryContext | null }) {
  const timeline = buildTimeline(context);
  if (timeline.length === 0) {
    return (
      <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-faint)', lineHeight: 1.6 }}>
        No related memory yet. Links will appear as this note connects to
        threads and other captures.
      </div>
    );
  }
  return (
    <>
      <SectionLabel>Related from memory</SectionLabel>
      <Timeline items={timeline.slice(0, 5)} />
    </>
  );
}

// Building blocks

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '1.3px',
        textTransform: 'uppercase',
        color: 'var(--ink-faint)',
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
        color: 'var(--ink-faint)',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        fontSize: 13,
        color: 'var(--ink-soft)',
        padding: '6px 0',
        lineHeight: 1.5,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: 'var(--accent)',
          marginTop: 8,
          flexShrink: 0,
        }}
      />
      <span>{children}</span>
    </div>
  );
}

function Timeline({ items }: { items: TimelineItem[] }) {
  return (
    <div style={{ position: 'relative', paddingLeft: 18 }}>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 4,
          top: 8,
          bottom: 8,
          width: 1,
          background: 'var(--rule)',
        }}
      />
      {items.map((item, i) => (
        <div
          key={item.id ?? `tl-${i}`}
          style={{ position: 'relative', paddingBottom: 16 }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: -18,
              top: 6,
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: 'var(--paper)',
              border: '1.5px solid var(--accent)',
            }}
          />
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--ink-faint)',
              marginBottom: 3,
            }}
          >
            <span>{item.label}</span>
            {item.date && (
              <span
                style={{
                  marginLeft: 8,
                  paddingLeft: 8,
                  borderLeft: '1px solid var(--rule)',
                }}
              >
                {item.date}
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--ink)',
              lineHeight: 1.55,
              textWrap: 'pretty',
            }}
          >
            {item.content}
          </div>
        </div>
      ))}
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
      <div className="skeleton" style={{ height: 44, borderRadius: 8, width: '70%' }} />
      <div className="skeleton" style={{ height: 68, borderRadius: 10 }} />
      <div className="skeleton" style={{ height: 20, borderRadius: 4, width: '50%' }} />
      <div className="skeleton" style={{ height: 64, borderRadius: 8 }} />
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: 'color-mix(in srgb, var(--error) 8%, transparent)',
        border: '1px solid color-mix(in srgb, var(--error) 20%, transparent)',
        borderRadius: 8,
        fontSize: 13,
        color: 'var(--error)',
      }}
    >
      {message}
    </div>
  );
}

// Derivations

interface TimelineItem {
  id?: string;
  label: string;
  date?: string;
  content: string;
}

function buildTimeline(context: MemoryContext | null): TimelineItem[] {
  if (!context) return [];
  const pile: MemoryItem[] = [
    ...(context.meetings ?? []),
    ...(context.insights ?? []),
    ...(context.commitments ?? []),
    ...(context.related ?? []),
  ];
  return pile.slice(0, 8).map((m) => ({
    id: m.id,
    label: formatKind(m.metadata.type),
    date: formatDate(m.metadata.createdAt),
    content: m.content,
  }));
}

function formatKind(type?: string): string {
  if (!type) return 'Note';
  if (type === 'meeting_note') return 'Meeting';
  if (type === 'commitment') return 'Commitment';
  if (type === 'insight') return 'Insight';
  if (type === 'context') return 'Context';
  return 'Note';
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function buildEmailNarrative(
  personName: string,
  context: MemoryContext | null
): React.ReactNode | null {
  if (!context || !personName) return null;
  const commits = context.commitments ?? [];
  const firstPerson = (context.person ?? [])[0];
  if (!commits.length && !firstPerson) return null;
  const pieces: string[] = [];
  pieces.push(`You and ${personName.split(' ')[0]} are in the middle of work together.`);
  if (commits.length > 0) {
    pieces.push(
      `${commits.length} open commitment${commits.length === 1 ? '' : 's'} on record.`
    );
  }
  if (firstPerson?.content) {
    pieces.push(firstPerson.content);
  }
  return pieces.join(' ');
}
